import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminServicesApi, adminStaffApi, adminCentersApi, adminCustomersApi, adminAuditApi, adminRoomsApi } from '@api/client';
import type {
  Room,
  Service,
  Staff,
  Center,
  CentersOverview,
  Customer,
  AuditLogEntry,
  PaginatedResponse,
  AuditLogFilters,
} from '@api/types';

// ---------------------------------------------------------------------------
// Services — catálogo GLOBAL post-0028 (sin scope per-centro).
// ---------------------------------------------------------------------------
export function useServices() {
  return useQuery<Service[]>({
    queryKey: ['services'],
    queryFn: () => adminServicesApi.list() as Promise<Service[]>,
    staleTime: 60_000,
  });
}

export function useCreateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => adminServicesApi.create(data),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['services'] }),
  });
}

export function useUpdateService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      adminServicesApi.update(id, data),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['services'] }),
  });
}

export function useDeleteService() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminServicesApi.delete(id),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['services'] }),
  });
}

// ---------------------------------------------------------------------------
// Staff
// ---------------------------------------------------------------------------
// `centerId` is only used for the queryKey/cache scope — the list endpoint
// resolves the caller's scope from the JWT. Always enabled so superadmin
// (whose `centerId` is '') still loads the cross-centre staff list.
export function useStaff(centerId: string) {
  return useQuery<Staff[]>({
    queryKey: ['staff', centerId],
    queryFn: () => adminStaffApi.list() as Promise<Staff[]>,
    staleTime: 60_000,
  });
}

export function useCreateStaff(centerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => adminStaffApi.create(data),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['staff', centerId] }),
  });
}

export function useUpdateStaff(centerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      adminStaffApi.update(id, data),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['staff', centerId] }),
  });
}

export function useDeleteStaff(centerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminStaffApi.delete(id),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['staff', centerId] }),
  });
}

// Superadmin only — assign/remove a staff member to/from an additional centre.
export function useAssignStaffCenter(centerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ staffId, targetCenterId }: { staffId: string; targetCenterId: string }) =>
      adminStaffApi.addCenter(staffId, targetCenterId),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['staff', centerId] }),
  });
}

export function useRemoveStaffCenter(centerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ staffId, targetCenterId }: { staffId: string; targetCenterId: string }) =>
      adminStaffApi.removeCenter(staffId, targetCenterId),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['staff', centerId] }),
  });
}

// ---------------------------------------------------------------------------
// Rooms
// ---------------------------------------------------------------------------
export function useRooms(centerId: string) {
  return useQuery<Room[]>({
    queryKey: ['rooms', centerId],
    queryFn: () => adminRoomsApi.list(),
    enabled: !!centerId,
    staleTime: 60_000,
  });
}

export function useCreateRoom(centerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string }) => adminRoomsApi.create(data),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['rooms', centerId] }),
  });
}

export function useUpdateRoom(centerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; isActive?: boolean } }) =>
      adminRoomsApi.update(id, data),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['rooms', centerId] }),
  });
}

export function useDeleteRoom(centerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminRoomsApi.delete(id),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['rooms', centerId] }),
  });
}

// ---------------------------------------------------------------------------
// Centers (superadmin)
// ---------------------------------------------------------------------------
// `enabled` lets superadmin-only callers (e.g. StaffPage) skip the request for
// center_admin, who would get 403 from `GET /api/admin/centers`.
/** Superadmin dashboard for /admin/centers — centres + counts in one call. */
export function useCentersOverview(enabled = true) {
  return useQuery<CentersOverview>({
    queryKey: ['centers-overview'],
    queryFn: () => adminCentersApi.overview(),
    enabled,
    staleTime: 30_000,
  });
}

export function useCenters(enabled = true) {
  return useQuery<Center[]>({
    queryKey: ['centers'],
    queryFn: async () => {
      const res = (await adminCentersApi.list()) as PaginatedResponse<Center>;
      return res.data;
    },
    enabled,
    staleTime: 60_000 * 5,
  });
}

export function useCreateCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => adminCentersApi.create(data),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['centers'] });
      void qc.invalidateQueries({ queryKey: ['centers-overview'] });
    },
  });
}

export function useUpdateCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      adminCentersApi.update(id, data),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['centers'] });
      void qc.invalidateQueries({ queryKey: ['centers-overview'] });
    },
  });
}

export function useDeleteCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminCentersApi.delete(id),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['centers'] });
      void qc.invalidateQueries({ queryKey: ['centers-overview'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------
export function useCustomers(params: Record<string, string> = {}) {
  return useQuery({
    queryKey: ['customers', params],
    queryFn: () => adminCustomersApi.list(params) as Promise<PaginatedResponse<Customer>>,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------
function auditFiltersToParams(filters: AuditLogFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.action) params.action = filters.action;
  if (filters.entityType) params.entityType = filters.entityType;
  if (filters.actorId) params.actorId = filters.actorId;
  if (filters.centerId) params.centerId = filters.centerId;
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  if (filters.page !== undefined) params.page = String(filters.page);
  if (filters.perPage !== undefined) params.perPage = String(filters.perPage);
  return params;
}

export function useAuditLog(filters: AuditLogFilters = {}) {
  return useQuery({
    queryKey: ['audit-log', filters],
    queryFn: () =>
      adminAuditApi.list(auditFiltersToParams(filters)) as Promise<PaginatedResponse<AuditLogEntry>>,
    staleTime: 0,
  });
}
